/**
 * Y-only re-pin of mirror-dependent lights after a mirror swap.
 *
 * Given existing mirror and light objects, recompute light Y values so
 * they stay pinned to the mirror (overhead lights sit OVERHEAD_GAP_ABOVE_MIRROR
 * above mirror top; sconces / wall lights clamp into the accepted sconce
 * height band). Returns only the objects whose centers changed — callers
 * apply them as updates.
 *
 * Synchronous preview/fallback helper — used by the web-app state
 * reducer during mirror-swap preview (before commit) so the user sees
 * lights re-pin live without a cloud round-trip. The full XZ+Y reflow
 * lives in the cloud `repositionLightsToMirror` helper, invoked by
 * `swap_fixture componentType: 'mirror'` on commit. Pure — no I/O.
 */

const {
  getSconceCenterY,
  computeOverheadLightCenterY,
} = require('./accessoryRules');
const { isOverheadLight } = require('./fixtureGroup');

/**
 * @param {Object} vanityContext
 * @param {number} vanityContext.floorY
 * @param {string} [vanityContext.fixtureGroupId]
 * @param {Object} roomContext
 * @param {Object} roomContext.existingAssembly
 * @param {Array} roomContext.existingAssembly.mirrors
 * @param {Array} roomContext.existingAssembly.lights
 * @returns {{
 *   objects: Array,
 *   componentsCreated: string[],
 *   fixtureGroupId: string,
 *   mirrors: Array,
 *   lights: Array,
 *   removedIds: string[],
 * }}
 */
function repositionVanityAccessories(vanityContext, roomContext) {
  const existing = roomContext?.existingAssembly;
  if (!existing) {
    throw new Error(
      '[repositionVanityAccessories] requires roomContext.existingAssembly'
    );
  }

  const mirrors = existing.mirrors || [];
  const lights = existing.lights || [];
  const floorY = vanityContext.floorY;

  const updatedObjects = [];

  for (const light of lights) {
    let nearestMirror = null;
    let nearestDistSq = Infinity;
    for (const m of mirrors) {
      const dx = (m.center?.x ?? 0) - (light.center?.x ?? 0);
      const dz = (m.center?.z ?? 0) - (light.center?.z ?? 0);
      const dSq = dx * dx + dz * dz;
      if (dSq < nearestDistSq) {
        nearestDistSq = dSq;
        nearestMirror = m;
      }
    }
    if (!nearestMirror) continue;

    let newY;
    if (isOverheadLight(light)) {
      newY = computeOverheadLightCenterY(
        nearestMirror.center?.y ?? 0,
        nearestMirror.dimensions?.y ?? 0,
        light.dimensions?.y ?? 0
      );
    } else {
      newY = getSconceCenterY(nearestMirror.center?.y ?? 0, floorY);
    }

    if (Math.abs(newY - (light.center?.y ?? 0)) < 1e-6) continue;

    updatedObjects.push({
      ...light,
      center: { ...light.center, y: newY },
    });
  }

  return {
    objects: updatedObjects,
    componentsCreated: [],
    fixtureGroupId: vanityContext.fixtureGroupId,
    mirrors: [],
    lights: updatedObjects,
    removedIds: [],
  };
}

module.exports = { repositionVanityAccessories };
